// src/pages/B2B/crm/components/SelectWithOther.tsx
import React, { useEffect, useState } from 'react';

const OTHER_SENTINEL = '__other__';

const SELECT_CLS =
  'w-full bg-[#0f0f1a] border border-[#2a2a3e] rounded px-2 py-1.5 text-sm text-gray-200';
const INPUT_CLS =
  'w-full bg-[#0f0f1a] border border-[#2a2a3e] rounded px-2 py-1.5 text-sm text-gray-200 mt-1';

interface Props {
  value: string | null;
  onChange: (v: string | null) => void;
  options: readonly string[];
  placeholder?: string;
  otherPlaceholder?: string;
}

/**
 * Dropdown of curated suggestions plus an "Other…" escape hatch that reveals a
 * free-text input. Controlled component — `value` is the literal string sent
 * to the API; "Other" mode is inferred when `value` is non-empty and not in
 * `options`.
 */
export function SelectWithOther({
  value,
  onChange,
  options,
  placeholder = '—',
  otherPlaceholder = 'Enter custom value',
}: Props) {
  const [otherMode, setOtherMode] = useState(
    !!value && !options.includes(value),
  );

  useEffect(() => {
    setOtherMode(!!value && !options.includes(value));
  }, [value, options]);

  const selectValue = otherMode ? OTHER_SENTINEL : (value ?? '');

  return (
    <>
      <select
        className={SELECT_CLS}
        value={selectValue}
        onChange={(e) => {
          const v = e.target.value;
          if (v === OTHER_SENTINEL) {
            setOtherMode(true);
            // Clear preset value so the text box starts empty.
            if (value && options.includes(value)) onChange(null);
          } else {
            setOtherMode(false);
            onChange(v || null);
          }
        }}
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
        <option value={OTHER_SENTINEL}>Other…</option>
      </select>
      {otherMode && (
        <input
          type="text"
          className={INPUT_CLS}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
          placeholder={otherPlaceholder}
        />
      )}
    </>
  );
}
